import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { IFlipInfo } from '@src/slide/application/service/edit-action-services/element-info';
import { IPoint, ISizeMap, IResizableSize } from './resizable.interface';
import ResizeLogic from './resize-logic';

const DEFAULT_ROTATE = 0;

export interface IResizeLogicPcProps {
    /**
     * 缩放比例
     */
    scale: number,
    /**
     * 旋转角度
     */
    rotate?: number,
    /**
     * 是否正在裁剪
     */
    isCroping?: boolean,
    /**
     * 相对水平翻转信息，用于组合子元素
     */
    relativeFlipH: number,
    /**
     * 相对垂直翻转信息，用于组合子元素
     */
    relativeFlipV: number,
    /**
     * 相对旋转角度信息，用于组合子元素
     */
    relativeRotate?: number,
    /**
     * 元素初始大小信息
     */
    startSize: IResizableSize;
    /**
     * 元素初始翻转信息
     */
    startFlip: IFlipInfo;
    /**
     * 缩放点
     */
    resizeMode: string;
    /**
     * 是否处于iFrame中
     */
    isIframe?: boolean,
    /**
     * 选中元素是否锁定纵横比
     */
    modelIsLockAspect: number,
    /**
    * 缩放过程中的事件回调
    */
    onResize: (e, startSize: IResizableSize, nextSize: IResizableSize, resizeMode: string, sizeMap?: ISizeMap) => void,
}

export default class ResizeLogicPC extends ResizeLogic {
    private readonly startSize: IResizableSize;
    private readonly scale: number;
    private readonly relativeRotate: number;
    private readonly relativeFlipH: number;
    private readonly relativeFlipV: number;
    private readonly isIframe?: boolean;
    private readonly rotate?: number;
    private readonly flipAtStart: IFlipInfo;
    private readonly modelIsLockAspect: number;
    private readonly resizeMode: string;
    private readonly onResize: (e, startSize: IResizableSize, nextSize: IResizableSize,
        resizeMode: string, sizeMap?: ISizeMap) => void;
    private readonly isCroping?: boolean;
    private transformedRect;    // 初始状态旋转后的rect
    private isExistGroup: boolean;
    private startMousePosition: IPoint; // 记录鼠标初始位置
    private sizeAtStart: IResizableSize;


    public constructor(props: IResizeLogicPcProps) {
        super();
        this.scale = props.scale;
        this.relativeRotate = props.relativeRotate ?? DEFAULT_ROTATE;
        this.relativeFlipH = props.relativeFlipH;
        this.relativeFlipV = props.relativeFlipV;
        this.isIframe = props.isIframe;
        this.rotate = props.rotate;
        this.startSize = props.startSize;
        this.flipAtStart = props.startFlip;
        this.modelIsLockAspect = props.modelIsLockAspect;
        this.resizeMode = props.resizeMode;
        this.onResize = props.onResize;
        this.isCroping = props.isCroping;
    }

    public startResize(e) {
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('touchmove', this.handleMouseMove, { passive: false });

        this.startMousePosition = this.getEventCoordinates(e);
        this.sizeAtStart = { ...this.startSize };
        // /先把左上顶点置0，   然后再给还原
        this.sizeAtStart.left = 0;
        this.sizeAtStart.top = 0;

        const selectedGroupModel = this.getSelectionService()
            ?.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP);
        this.isExistGroup = (selectedGroupModel.length > 0);

        this.initSizeInfoAtStartMap();
        // 计算初始状态旋转后的rect
        this.transformedRect = this.getRransformedRect(this.sizeAtStart, this.isCroping, this.rotate);
    }

    public stopResize = () => {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('touchmove', this.handleMouseMove);
    };

    private handleMouseMove = (e) => {
        if (!this.transformedRect) {
            return;
        }
        const windowZoom = 1;
        const nex = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
        const ney = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
        let offsetX = (nex - this.startMousePosition.x) / this.scale / windowZoom;
        let offsetY = (ney - this.startMousePosition.y) / this.scale / windowZoom;

        if (this.isExistGroup) {
            // 外层rotate修正
            const angle = this.relativeRotate * Math.PI / 180;
            const cosAngle = Math.cos(angle);
            const sinAngle = Math.sin(angle);
            const tempX = (offsetX * cosAngle) + (offsetY * sinAngle);
            const tempY = (offsetY * cosAngle) - (offsetX * sinAngle);
            offsetX = tempX;
            offsetY = tempY;

            // 外层FlipH修正
            if (this.relativeFlipH === 1) {
                offsetX = -offsetX;
            }

            // 外层FlipV修正
            if (this.relativeFlipV === 1) {
                offsetY = -offsetY;
            }
        }

        const sizeParams = {
            e,
            flipAtStart: this.flipAtStart,
            isCroping: this.isCroping,
            isIframe: this.isIframe,
            modelIsLockAspect: this.modelIsLockAspect,
            offsetX,
            offsetY,
            resizeMode: this.resizeMode,
            rotate: this.rotate,
            sizeAtStart: this.sizeAtStart,
            startSize: this.startSize,
        };

        // 记录所有选中元素的nextSize
        const sizeMap = this.getSizeMap(sizeParams);
        const { nextSize } = this.getAllSize(sizeParams);
        this.onResize(e, this.startSize, nextSize, this.resizeMode, sizeMap);
    };
}
